<script setup>
import GeneralLink from 'multisite/components/cmsContentBlocks/generalLink.vue';
import { DsBadge, DsText } from '@coloplast/design-system';

defineProps({
	url: String,
	vm: Object,
	variant: Object,
	properties: {
		type: Object,
		default: null,
	},
	index: {
		type: Number,
		default: undefined,
	},
});

const getDataCtaBehavior = (linkBehavior) => {
	return linkBehavior === 'RedirectToLink' ? 'redirect-to-link' : 'add-sample';
};
const getVariant = (linkBehavior) => {
	return linkBehavior === 'RedirectToLink' ? '' : 'secondary';
};
</script>
<template>
	<article
		class="c-card-slider-product-card"
		data-testid="data-card-slider-product-card"
		itemtype="https://schema.org/Product"
		:data-index="index"
	>
		<figure class="c-card-slider-product-card__figure">
			<img
				itemprop="image"
				:alt="properties.Header"
				:src="properties.ImageUrl"
				class="c-card-slider-product-card__figure__image"
				loading="lazy"
			/>
			<figcaption v-if="properties.BadgeText" class="c-card-slider-product-card__label">
				<DsBadge :text="properties.BadgeText" />
			</figcaption>
		</figure>
		<div class="c-card-slider-product-card__content">
			<div class="c-card-slider-product-card__info">
				<DsText
					v-if="properties.Manufacturer"
					class="c-card-slider-product-card__info__brand"
					html-element="span"
					text-style="regular"
					text-type="body-xs"
				>
					{{ properties.Manufacturer }}
				</DsText>
				<DsText
					class="c-card-slider-product-card__info__header"
					html-element="h3"
					text-style="bold"
					text-type="heading-md"
				>
					{{ properties.Header }}
				</DsText>
				<DsText
					html-element="div"
					text-type="body-sm"
					class="c-card-slider-product-card__info__description"
				>
					{{ properties.Description }}
				</DsText>
			</div>

			<div class="c-card-slider-product-card__footer">
				<GeneralLink
					:link-settings="{
						LinkBehavior: properties.LinkBehavior,
						AriaLabel: getDataCtaBehavior(properties.LinkBehavior),
						DisplayCartItem: properties.DisplayCartItem,
						LinkLabel: properties.ButtonLabel,
						LoginRequiredForAddToBasket: properties.LoginRequiredForAddToBasket
					}"
					:icon-end="'shopping-basket'"
					:url="properties.Url"
					:button-variant="getVariant(properties.LinkBehavior)"
					:variant="properties.AddItemToBasketRequest"
					size="sm"
				>
				</GeneralLink>
			</div>
		</div>
	</article>
</template>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-card-slider-product-card {
	overflow: hidden;
	position: relative;
	display: flex;
	flex-flow: column nowrap;
	justify-content: stretch;
	margin: 0;
	width: 100%;
	height: 100%;
	min-height: 403px;
	border-radius: 8px;
	border: 1px solid ds-color('border', 'muted');
	background: var(--color-bg-elevation-raised, #fff);

	@include t-respond-to-max('medium') {
		min-height: 316px;
	}

	&__figure {
		height: 216px;
		background-color: ds-color('bg', 'decorative-1-muted');
		margin: 0;
		overflow: hidden;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;

		&__image {
			display: block;
			width: auto;
			height: inherit;
			mix-blend-mode: multiply;
			filter: brightness(105%);
		}
	}

	&__label {
		position: absolute;
		inset-block-start: 12px;
		inset-inline-end: 8px;
		margin: 0 ds-space(4) 0 ds-space(6);
	}

	&__content {
		display: flex;
		flex: 1;
		flex-flow: column nowrap;
		justify-content: space-between;
	}

	&__info {
		padding: 16px;

		&__brand {
			margin-bottom: 4px;
		}

		&__brand,
		&__description {
			color: ds-color('fg', 'muted');
		}

		&__header {
			margin-bottom: 8px;
		}

		&__description {
			display: -webkit-box;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	&__footer {
		display: flex;
		padding: 8px 16px 24px;

		a[aria-label='redirect-to-link'] {
			position: absolute;
			inset: 0;
			width: 100%;
			opacity: 0;

			& > span {
				position: absolute;
				overflow: hidden;
				clip: rect(0 0 0 0);
				height: 1px;
				width: 1px;
				margin: -1px;
				padding: 0;
				border: 0;
			}
		}
	}
}
</style>
